import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BASE_API, GET_NEW_INWARD_BY_ID_API } from '../config/API';
import { MdArrowBack, MdDownload, MdEdit } from 'react-icons/md';
import Loader from '../components/common/Loader';
import './InwardEntry.css'; // Use shared inward styles for consistent preview
import { formatDate } from '../utils/dateUtils';

const ViewInward = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [inwardData, setInwardData] = useState(null);

    useEffect(() => {
        const fetchInwardDetails = async () => {
            try {
                setLoading(true);
                const token = localStorage.getItem('accessToken');
                const response = await axios.get(GET_NEW_INWARD_BY_ID_API(id), {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data.status) {
                    const data = response.data.data;
                    
                    // Map items directly from the rich inward response
                    if (data.InwardItems) {
                        data.InwardItems = data.InwardItems.map(item => ({
                            ...item,
                            Product: item.Product || { productName: `Product #${item.productId}` },
                            Unit: item.Unit || { unitName: 'Nos' },
                            orderedQty: Number(item.orderedQty) || 0,
                            receivedTotal: Number(item.totalReceivedAllInwards || item.receivedTotal) || 0,
                            pendingQty: Number(item.pendingQuantityOverall || item.pendingQty || 0),
                            previousTotalReceived: Number(item.previousTotalReceived) || 0
                        }));
                    }

                    setInwardData(data);
                }
            } catch (error) {
                console.error('Error fetching inward details:', error);
            } finally {
                setLoading(false);
            }
        };

        if (id) fetchInwardDetails();
    }, [id]);


    if (!inwardData) return (
        <div className="add-po-page">
            <div className="page-header">
                <button onClick={() => navigate('/inward-management')} className="back-btn">
                    <MdArrowBack />
                </button>
                <h2>Inward Entry Not Found</h2>
            </div>
        </div>
    );

    return (
        <div className="add-po-page">
            <div className="page-header">
                <button onClick={() => navigate('/inward-management')} className="back-btn">
                    <MdArrowBack />
                </button>
                <h2>Inward Details: {inwardData.inwardNo || `#${inwardData.id}`}</h2>
                <div className="view-po-header-actions" style={{ marginLeft: 'auto' }}>
                    <button
                        className="edit-toggle-btn"
                        onClick={() => navigate(`/inward/entry/${id}`)}
                    >
                        <MdEdit /> Edit Mode
                    </button>
                </div>
            </div>

            <div className="project-form">
                <div className="form-section">
                    <h3>General Details</h3>
                    <div className="form-grid">
                        <div className="form-group">
                            <label>Vendor Name</label>
                            <div className="view-value">
                                {inwardData.Vendor?.vendorName || inwardData.PurchaseOrder?.Vendor?.vendorName || inwardData.vendorName || 'N/A'}
                            </div>
                        </div>
                        <div className="form-group">
                            <label>Purchase Order</label>
                            <div className="view-value">
                                #{inwardData.PurchaseOrder?.poNumber || inwardData.purchaseOrderId || 'N/A'}
                            </div>
                        </div>
                        <div className="form-group">
                            <label>PO Date</label>
                            <div className="view-value">
                                {formatDate(inwardData.PurchaseOrder?.date)}
                            </div>
                        </div>
                        <div className="form-group">
                            <label>Project Name</label>
                            <div className="view-value">
                                {inwardData.ProjectMaster?.projectName || inwardData.Project?.projectName || inwardData.PurchaseOrder?.ProjectMaster?.projectName || inwardData.PurchaseOrder?.Project?.projectName || 'N/A'}
                            </div>
                        </div>
                        <div className="form-group full-width">
                            <label>Remarks / Notes</label>
                            <div className="view-value" style={{ minHeight: '60px' }}>
                                {inwardData.remarks || 'No remarks provided.'}
                            </div>
                        </div>
                        <div className="form-group">
                            <label>Status</label>
                            <div className="view-value">
                                <span className={`status-badge status-${(inwardData.status || 'pending').toLowerCase()}`}>
                                    {inwardData.status || 'Pending'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="form-section">
                    <h3>Delivery Challan</h3>
                    <div className="form-grid">
                        <div className="form-group full-width">
                            {inwardData.challanFile ? (
                                <div className="preview-container">
                                    <div className="preview-header">
                                        <span className="preview-label">Delivery Challan:</span>
                                        <div className="preview-actions">
                                            <button
                                                type="button"
                                                className="preview-action-btn view-full-btn"
                                                onClick={() => window.open((inwardData.challanFile.startsWith('https') ? inwardData.challanFile : `${BASE_API}/uploads/challans/${inwardData.challanFile}`), '_blank')}
                                            >
                                                View Full Screen
                                            </button>
                                        </div>
                                    </div>
                                    {(inwardData.challanFile.match(/\.(jpg|jpeg|png)$/i)) ? (
                                        <img
                                            src={(inwardData.challanFile.startsWith('http') ? inwardData.challanFile : `${BASE_API}/uploads/challans/${inwardData.challanFile}`)}
                                            alt="Challan Preview"
                                            className="challan-preview-img"
                                        />
                                    ) : (inwardData.challanFile.match(/\.pdf$/i)) ? (
                                        <div className="pdf-preview-wrapper">
                                            <iframe
                                                src={(inwardData.challanFile.startsWith('http') ? inwardData.challanFile : `${BASE_API}/uploads/challans/${inwardData.challanFile}`)}
                                                title="Challan PDF Preview"
                                                className="challan-pdf-iframe"
                                            ></iframe>
                                        </div>
                                    ) : (
                                        <div className="challan-file-badge">{inwardData.challanFile}</div>
                                    )}
                                </div>
                            ) : (
                                <div className="view-value">No challan uploaded.</div>
                            )}
                        </div>
                    </div>
                </div>

                {inwardData.InwardItems && inwardData.InwardItems.length > 0 && (
                    <div className="form-section">
                        <h3>Received Items</h3>
                        <div className="items-table-wrapper">
                            <table className="items-table">
                                <thead className="thead-po">
                                    <tr>
                                        <th style={{ width: '50px' }}>SR No</th>
                                        <th>Product Name</th>
                                        <th style={{ width: '120px' }}>Ordered Qty</th>
                                        <th style={{ width: '140px' }}>Cumulative Received</th>
                                        <th style={{ width: '120px' }}>Pending Qty</th>
                                        <th style={{ width: '100px' }}>Received (This Entry)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {inwardData.InwardItems.map((item, index) => (
                                        <tr key={index}>
                                            <td style={{ textAlign: 'center' }}>{index + 1}</td>
                                            <td className="item-name-cell">
                                                {item.Product?.productName || 'N/A'}
                                            </td>
                                            <td className="qty-display">{Number(item.orderedQty || 0).toFixed(2)} {item.Unit?.unitName || item.Product?.Unit?.unitName || 'Nos'}</td>
                                            <td>{Number(item.previousTotalReceived || 0).toFixed(2)}</td>
                                            <td>{Number(item.orderedQty - item.previousTotalReceived || 0).toFixed(2)}</td>
                                            <td className="receive-now-cell">
                                                <span className="receive-now-value" style={{ fontWeight: '700', color: '#6366f1' }}>
                                                    {Number(item.receiveNow || 0).toFixed(2)}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile View Cards */}
                        <div className="mobile-items-view">
                            {inwardData.InwardItems.map((item, index) => (
                                <div key={index} className="mobile-item-card">
                                    <div className="mobile-item-header">
                                        <div className="mobile-item-title">
                                            <span className="sr-badge">Item #{index + 1}</span>
                                            <strong>{item.Product?.productName || 'N/A'}</strong>
                                        </div>
                                    </div>
                                    <div className="mobile-item-body">
                                        <div className="mobile-item-row">
                                            <span className="m-label">Ordered Qty</span>
                                            <span className="m-value">{Number(item.orderedQty || 0).toFixed(2)} {item.Unit?.unitName || item.Product?.Unit?.unitName || 'Nos'}</span>
                                        </div>
                                        <div className="mobile-item-row">
                                            <span className="m-label">Cumulative Received</span>
                                            <span className="m-value">{Number(item.previousTotalReceived || 0).toFixed(2)}</span>
                                        </div>
                                        <div className="mobile-item-row">
                                            <span className="m-label">Pending Qty</span>
                                            <span className="m-value">{Number(item.orderedQty - item.previousTotalReceived || 0).toFixed(2)}</span>
                                        </div>
                                        <div className="mobile-item-row full-width">
                                            <span className="m-label">Received (This Entry)</span>
                                            <span className="m-value" style={{ color: '#6366f1', fontSize: '1.1rem' }}>
                                                {Number(item.receiveNow || 0).toFixed(2)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ViewInward;
